import { Btn, Bx, Input, Typo } from '@curry-group/mui-curcuma';
import { Grid } from '@material-ui/core';
import { faSearch, faTimes, faUserCheck } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchComplaintContact, searchComplaintContacts, setComplaintContact } from '../../data/reducer/userprofile';
import { NotFoundComponent } from '../detail/not-found';

/**
 * Admin area: picks the user that complaints about bot answers are routed to.
 * Without a contact the bot chat hides its operator link entirely.
 */
export const ComplaintContactAdminComponent: React.FC = () => {
  const dispatch = useDispatch();
  const isAdmin = useSelector(state => state.foundation?.profile?.isAdmin);
  const contact = useSelector(state => state.userProfile?.complaintContact);
  const working = useSelector(state => state.userProfile?.complaintContactWorking);
  const candidates = useSelector(state => state.userProfile?.complaintContactCandidates) || [];
  const searching = useSelector(state => state.userProfile?.complaintContactSearching);
  const [query, setQuery] = useState('');

  useEffect(() => {
    if (!isAdmin) return;
    dispatch(fetchComplaintContact({}));
  }, [dispatch, isAdmin]);

  if (!isAdmin) {
    return <NotFoundComponent />;
  }

  const onSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (query.trim().length < 2) return;
    dispatch(searchComplaintContacts({ q: query.trim() }));
  };

  return (
    <Bx pb={4}>
      <Bx pb={2}>
        <Typo variant={'h3'} component={'h3'}>
          Helpdesk der Fachredaktion in der Rubrik Bot
        </Typo>
      </Bx>
      <Bx pb={2}>
        <Typo variant="body2" color="textSecondary">
          Diese Person wird in der Rubrik Bot als Ansprechpartner der Fachredaktion angegeben.
          Beim Klick auf “Fachredaktion” in der Präambel wird ein Chat mit ihr geöffnet.
          Die Fachredaktion nimmt ohne manuelle Annahme der Einladung am Chat teil.
          Ist kein Ansprechpartner ausgewiesen, wird der Link in der Präambel der Rubrik Bot nicht angezeigt.
        </Typo>
      </Bx>

      <Bx pb={2} display="flex" alignItems="center" gridGap={12} flexWrap="wrap">
        <Typo variant="body1" component="span" fontWeight={500}>
          {working
            ? 'Wird geladen …'
            : contact?.userId
            ? `Aktuell: ${contact.fullName || contact.userId}${contact.email ? ` (${contact.email})` : ''}`
            : 'Aktuell ist kein Ansprechpartner hinterlegt.'}
        </Typo>
        {!!contact?.userId && !working && (
          <Btn
            size="small"
            variant="outlined"
            color="default"
            startIcon={<FontAwesomeIcon icon={faTimes} />}
            onClick={() => dispatch(setComplaintContact({ userId: undefined }))}
            title="Ansprechpartner entfernen"
          >
            Entfernen
          </Btn>
        )}
      </Bx>

      <form onSubmit={onSearch}>
        <Grid container spacing={2} alignItems="flex-end">
          <Grid item xs={12} md={8}>
            <Input
              inputLabel={{ children: 'Person suchen (Name oder E-Mail)' }}
              input={{
                value: query,
                fullWidth: true,
                onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setQuery(e.target.value),
                placeholder: 'mindestens 2 Zeichen …',
              }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <Btn type="submit" size="large" fullWidth variant="outlined" disabled={!!searching} startIcon={<FontAwesomeIcon icon={faSearch} />}>
              {searching ? 'Suche …' : 'Suchen'}
            </Btn>
          </Grid>
        </Grid>
      </form>

      {!searching && !!candidates.length && (
        <Bx mt={2}>
          {candidates.map(candidate => (
            <Bx key={candidate.userId} py={1} display="flex" alignItems="center" justifyContent="space-between" gridGap={8} flexWrap="wrap">
              <Typo variant="body2" component="span">
                {candidate.fullName || candidate.userId}
                {candidate.email ? ` — ${candidate.email}` : ''}
              </Typo>
              <Btn
                size="small"
                variant={candidate.userId === contact?.userId ? 'contained' : 'outlined'}
                color="primary"
                disabled={candidate.userId === contact?.userId || !!working}
                startIcon={<FontAwesomeIcon icon={faUserCheck} />}
                onClick={() => dispatch(setComplaintContact({ userId: candidate.userId }))}
              >
                {candidate.userId === contact?.userId ? 'Ausgewählt' : 'Auswählen'}
              </Btn>
            </Bx>
          ))}
        </Bx>
      )}
      {!searching && !candidates.length && !!query && (
        <Bx mt={2}>
          <Typo variant="body2" color="textSecondary">
            Keine Person gefunden.
          </Typo>
        </Bx>
      )}
    </Bx>
  );
};
